import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'underscore';
import classnames from 'classnames';
import { useTranslation, withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';

import OrderModule from 'components/modules/orders/Module';
import ExceptionsActionCreators from 'actions/ExceptionsActionCreators';
import CargomaticPane from 'components/v2/pane';
import SessionStore from 'stores/SessionStore';
import ExceptionStore from 'stores/ExceptionStore';
import Container from 'components/v2/container';

import DataPoller from 'components/ui/DataPoller';
import DataList from 'components/ui/DataList';
import CustomerSearch from 'components/site/customer-search';
import { getColumns } from './OrderMapping';
import { getChangesColumns } from './ChangesMapping';
import ErrorAlert from '../../ui/ErrorAlert';
import OrderSearch from '../../site/order-search';
import Badge from '../../ui/Badge';

const OrderChangeException = (props) => {
  const {
    exception: {
      meta: { changes },
      message,
    },
  } = props;
  const { t } = useTranslation();

  return (
    <div className="p-8">
      {message && (
        <div className="mb-2">
          <ErrorAlert title={t('CHANGE_REQUEST_FAILED_REASON')}>
            {message}
          </ErrorAlert>
        </div>
      )}
      <h3 className="mb-4">{t('CHANGE_REQUEST')}</h3>
      <p className="mb-4">{t('CHANGE_REQUEST_RECEIVED')}</p>
      <div className="text-sm border-t border-b border-solid border-grey">
        <DataList
          showHeaders
          hover
          striped
          data={changes}
          columns={getChangesColumns()}
        />
      </div>
      <div className="flex justify-end py-4">
        <button
          type="button"
          className="mr-2 button button-hollow-red"
          onClick={props.onHide}
        >
          {t('CLOSE')}
        </button>
        <button
          type="button"
          className="button button-green"
          onClick={() => props.onResolve(props.exception._id)}
        >
          {t('RESOLVE')}
        </button>
      </div>
    </div>
  );
};

OrderChangeException.propTypes = {
  exception: PropTypes.shape({
    _id: PropTypes.string,
    message: PropTypes.string,
    meta: PropTypes.shape({
      changes: PropTypes.array,
    }),
  }),
  onHide: PropTypes.func.isRequired,
  onResolve: PropTypes.func.isRequired,
};

const GenericOrderException = (props) => {
  let changes = [];
  const { t } = useTranslation();

  if (props.exception && props.exception.meta) {
    changes = props.exception.meta.changes;
  }

  return (
    <div className="p-8">
      <h3>{t('GENERIC_EXCEPTION')}</h3>
      <div className="py-4">
        <span>{t('PROCESSING_DATA_ERROR')}</span>
      </div>
      <div className="text-sm border-t border-b border-solid border-grey">
        <DataList
          showHeaders
          hover
          striped
          data={changes}
          columns={getChangesColumns()}
        />
      </div>
      <div className="flex justify-end py-4">
        <button
          type="button"
          className="mr-2 button button-hollow-red"
          onClick={props.onHide}
        >
          {t('CLOSE')}
        </button>
        <button
          type="button"
          className="button button-green"
          onClick={() => props.onResolve(props.exception._id)}
        >
          {t('RESOLVE')}
        </button>
      </div>
    </div>
  );
};

GenericOrderException.propTypes = {
  exception: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    meta: PropTypes.shape({
      changes: PropTypes.array,
    }),
  }),
  onHide: PropTypes.func.isRequired,
  onResolve: PropTypes.func.isRequired,
};

class OrderExceptions extends Component {
  constructor(props) {
    super(props);
    const user = SessionStore.getUser();

    this.state = {
      user,
      selected: [],
      order_oid: null,
      showExceptionPane: false,
      showOrderPane: false,
      totalPages: 0,
      filters: {
        _type: 'order',
        sort_by: '_id',
        order: 'asc',
        orderId: null,
        shipper: null,
        page: 1,
        per_page: 20,
      },
      filterMetadata: {
        shipper: '',
        order: '',
      },
      exceptions: [],
    };
  }

  componentDidMount() {
    ExceptionStore.addChangeListener(this.onChange);
  }

  componentWillUnmount() {
    ExceptionStore.removeChangeListener(this.onChange);
  }

  handleSelectOrder = (_value, order) => {
    this.setState((prevState) => {
      const { filters: currentFilters, filterMetadata: currentFilterMetadata } =
        prevState;

      const filters = {
        ...currentFilters,
        orderId: order._id,
        page: 1,
      };

      const filterMetadata = {
        ...currentFilterMetadata,
        order: order.order_id,
      };

      return {
        filters,
        filterMetadata,
      };
    }, this.queryExceptions);
  };

  handleOrderClick = (row, e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({
      showExceptionPane: false,
      showOrderPane: true,
      order_oid: row.order._id,
    });
  };

  onChange = () => {
    const exceptions = _.sortBy(ExceptionStore.getAllExceptions(), '_id');
    const totalPages = ExceptionStore.getTotalPages();
    this.setState({ exceptions, totalPages });
  };

  queryExceptions = () => {
    const filters = { ...this.state.filters };
    if (!filters.shipper) {
      delete filters.shipper;
    }
    if (!filters.orderId) {
      delete filters.orderId;
    }
    ExceptionsActionCreators.getOrderExceptions(filters);
  };

  onPaginationSelect = (page) => {
    this.setState((prevState) => {
      return {
        filters: {
          ...prevState.filters,
          page,
        },
      };
    }, this.queryExceptions);
  };

  onHideExceptionPane = () => {
    this.setState({ showExceptionPane: false });
  };

  onRowSelect = (row, isSelected) => {
    const selected = isSelected ? [row._id] : [];
    const update = {
      selected,
      showExceptionPane: selected.length === 1,
    };

    return this.setState(update);
  };

  shouldRowHighlight = (row) => {
    if (!row) return;

    const { shipper, release_time } = row;
    const status = shipper && shipper.status ? shipper.status : '';
    const diff = moment().diff(moment(release_time), 'hours');

    const classNames = classnames({
      'bg-blue-100':
        ['Service Watch', 'New Customer'].indexOf(status) > -1 && !row.on_hold,
      'bg-yellow-200': row.on_hold,
      'bg-red-50': diff >= 1 && diff < 2 && !row.on_hold,
      'bg-red-100': diff >= 2 && diff < 3 && !row.on_hold,
      'bg-red-200': diff >= 3 && !row.on_hold,
      'cursor-pointer': true,
    });

    return classNames;
  };

  onHideOrderPane = () => {
    this.setState({ showOrderPane: false });
  };

  resolveException = (id) => {
    ExceptionsActionCreators.addResolution(id, 'Change Accepted', {});
    this.setState({ showExceptionPane: false }, () => {
      setTimeout(this.queryExceptions, 250);
    });
  };

  getSelectedExceptionComponent = () => {
    const { selected, exceptions } = this.state;
    if (!selected || selected.length === 0) return;
    const exception = _.findWhere(exceptions, { _id: selected[0] });
    if (exception._type !== 'generic') {
      return (
        <OrderChangeException
          onResolve={this.resolveException}
          exception={exception}
          onHide={this.onHideExceptionPane}
        />
      );
    }

    return (
      <GenericOrderException
        onResolve={this.resolveException}
        exception={exception}
        onHide={this.onHideExceptionPane}
      />
    );
  };

  formatColumnDefs(columns) {
    return columns.map((c) => {
      if (c.dataField.includes('order_id')) {
        c.formatExtraData = {
          onClick: this.handleOrderClick,
        };
      }

      return c;
    });
  }

  handleRemoveOrder = () => {
    this.setState((prevState) => {
      const { filters: currentFilters, filterMetadata: currentFilterMetadata } =
        prevState;

      const filters = {
        ...currentFilters,
        orderId: null,
        page: 1,
      };

      const filterMetadata = {
        ...currentFilterMetadata,
        order: null,
      };

      return {
        filters,
        filterMetadata,
      };
    }, this.queryExceptions);
  };

  handleRemoveShipper = () => {
    this.setState((prevState) => {
      const { filters: currentFilters, filterMetadata: currentFilterMetadata } =
        prevState;

      const filters = {
        ...currentFilters,
        shipper: null,
        page: 1,
      };

      const filterMetadata = {
        ...currentFilterMetadata,
        shipper: null,
      };

      return {
        filters,
        filterMetadata,
      };
    }, this.queryExceptions);
  };

  handleSelectShipper = (_label, item) => {
    this.setState((prevState) => {
      const { filters: currentFilters, filterMetadata: currentFilterMetadata } =
        prevState;

      const filters = {
        ...currentFilters,
        shipper: item._id,
        page: 1,
      };

      const filterMetadata = {
        ...currentFilterMetadata,
        shipper: item.name,
      };

      return {
        filters,
        filterMetadata,
      };
    }, this.queryExceptions);
  };

  render() {
    const { t } = this.props;
    if (!this.state.user) return <div>{t('LOADING')}</div>;

    const columnDefs = this.formatColumnDefs(getColumns());

    return (
      <Container className="py-8 min-h-screen">
        <DataPoller action={this.queryExceptions} />
        <header className="">
          <h3 className="mb-4 text-xl font-medium">{t('ORDER_EXCEPTIONS')}</h3>
        </header>
        <div className="px-4 -mx-4">
          <div className="flex flex-wrap filters-wrapper">
            <div className="w-full px-4 mb-4 md:w-1/3">
              <h4 className="mb-2 mb-4 text-lg">{t('SEARCH_BY_ORDER_ID')}</h4>
              {!this.state.filters.orderId && (
                <OrderSearch onSelect={this.handleSelectOrder} />
              )}
              {this.state.filters.orderId && (
                <div className="flex-grow-0 flex-shrink-0 inline-block">
                  <Badge
                    showDelete
                    onClick={this.handleRemoveOrder}
                    color="green"
                    text={this.state.filterMetadata.order}
                  />
                </div>
              )}
            </div>
            <div className="w-full px-4 mb-4 md:w-1/3">
              <h4 className="mb-4 text-lg">
                {titleize(t('SHIPPER', { count: 1 }))}
              </h4>
              {!this.state.filters.shipper && (
                <CustomerSearch
                  clearOnSelect
                  type="shippers"
                  onSelect={this.handleSelectShipper}
                  placeholder={t('SEARCH_FOR_SHIPPER_PLACEHOLDER')}
                />
              )}
              {this.state.filters.shipper && (
                <div className="flex-grow-0 flex-shrink-0 inline-block">
                  <Badge
                    showDelete
                    onClick={this.handleRemoveShipper}
                    color="green"
                    text={this.state.filterMetadata.shipper}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="w-full">
          <DataList
            shouldRowHighlight={this.shouldRowHighlight}
            showHeaders
            paginate
            select
            hover
            clickToSelect
            selectMode="radio"
            data={this.state.exceptions}
            columns={columnDefs}
            total={ExceptionStore.getTotalCount()}
            perPage={this.state.filters.per_page}
            pages={this.state.totalPages}
            page={this.state.filters.page}
            onPaginationSelect={this.onPaginationSelect}
            selected={this.state.selected}
            onSelect={this.onRowSelect}
          />
          <div id="pane-container" />
          {this.state.showExceptionPane && (
            <CargomaticPane
              show={this.state.showExceptionPane}
              title={t('EXCEPTION_DETAILS')}
              onHide={this.onHideExceptionPane}
              renderBody={this.getSelectedExceptionComponent}
              location="right"
              className="sm:w-1/2 sm:max-w-none"
            />
          )}
          {this.state.showOrderPane && (
            <CargomaticPane
              show
              location="left"
              title={titleize(t('DETAIL', { count: 1 }))}
              onHide={this.onHideOrderPane}
              renderBody={() => (
                <div className="p-6">
                  <OrderModule hideMap id={this.state.order_oid} />
                </div>
              )}
              width={0.8}
            />
          )}
        </div>
      </Container>
    );
  }
}
export default withTranslation()(OrderExceptions);
